/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useState} from 'react'
import clsx from 'clsx'
import {useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {useForm} from 'react-hook-form'
import {consoleLog} from 'utils/utility'
import {UiTabSwitcherItem} from 'components/Ui/UiTabSwitcher'
import {toast} from 'utils/toastUtils'
import {closeSecondaryModal} from 'helpers/actions'
import UiFormInner from 'components/Forms/UIFormInner'
import UiFormActionsBar from 'components/Forms/UiFormActionsBar'
import {useAppDispatch} from 'helpers/store'
import {OffersAction} from 'store/offers/OffersAction'
import {useTypedSelector} from 'helpers/reducers'
import UiSelectWithGraphics, {type UiSelectOption} from 'components/Ui/UiSelectWithGraphics'
import {PaymentType} from 'types/enums/PaymentType'
import UiTextarea from 'components/Ui/UiTextarea'

interface BookingSellerDuplicateOfferModalProps {
    offer_id: number
    title?: string
    options?: UiTabSwitcherItem[]
    defaultOption?: UiTabSwitcherItem
    closeModal?: () => void
}

const BookingSellerPaymentTypeModal = (props: BookingSellerDuplicateOfferModalProps) => {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const { t } = useTranslation()
    const [isSubmitting, setIsSubmitting] = useState(false)
    const selectedOffer = useTypedSelector((state) => state.offersState.selectedOffer)

    const { handleSubmit, setValue, watch, register } = useForm({
        defaultValues: {
            offer_id: selectedOffer?.id,
            payment_type: selectedOffer?.extra?.payment_type || 'all_in_one',
            custom_text: null,
        },
    })

    const selectedPaymentType = watch('payment_type')

    const customSelectedPaymentType = () => {
        const matching = [PaymentType.AllCheckout, PaymentType.OfferInvoice, PaymentType.AllInvoice].includes(
            selectedPaymentType as PaymentType
        )

        if (!matching) {
            setValue('payment_type', PaymentType.Individual)
            setValue('custom_text', matching ? '' : selectedOffer?.extra?.payment_type)
        }
    }

    const selectedCustomText = watch('custom_text')

    const itemsForOptions: UiSelectOption[] = [
        {
            value: PaymentType.AllInvoice,
            label: t('All items on invoice'),
            description: 'Ihr gewählter Abrechnungsmodus ist <b>Alles auf Gesamtrechnung</b>',
        },
        {
            value: PaymentType.OfferInvoice,
            label: t('Offer items on invoice'),
            description:
                'Ihr gewählter Abrechnungsmodus ist <b>Offertebestandteile auf Gesamtrechnung, restliche Konsumation zahlt jeder Teilnehmer selbst beim Check-out</b>',
        },
        {
            value: PaymentType.AllCheckout,
            label: t('All items on checkout'),
            description: 'Ihr gewählter Abrechnungsmodus ist <b>Alles wird vor Ort beim Check-out bezahlt</b>',
        },
        { value: PaymentType.Individual, label: t('Individually set in Control app') },
    ]

    const onSubmitHandler = () => {
        setIsSubmitting(true)
        if (!props.offer_id) {
            toast.error(t('Missing offer ID'))
            setIsSubmitting(false)
            return
        }

        dispatch(
            OffersAction.fetchChangeOfferPaymentType({
                offer_id: props.offer_id,
                payment_type: selectedPaymentType,
                custom_text: selectedCustomText,
            })
        )
            .then(
                () => {
                toast.success(t('Payment type has been successfully updated.'))
                    if (props.closeModal) {
                        props.closeModal()
                    } else {
                        dispatch(closeSecondaryModal())
                    }
                },
                () => {
                    toast.error(t('Error upon duplication.'))
                }
            )
            .catch((err) => {
                if (err?.status && err?.status === 401) {
                    navigate('/login', { replace: true })
                }
            })
            .finally(() => {
                setIsSubmitting(false)
            })
    }

    const onErrorHandler = (data: unknown) => {
        consoleLog('onErrorHandler', data)
    }

    const setSelectedOptionHandler = (value: string | number | undefined) => {
        if (value !== undefined) {
            setValue('payment_type', value as string)
        }
    }

    useEffect(() => {
        customSelectedPaymentType()
    }, [])

    return (
        <form
            className={clsx('BookingSellerDuplicateOfferModal flex flex-col grow')}
            onSubmit={handleSubmit(onSubmitHandler, onErrorHandler)}
        >
            <UiFormInner>
                <div className={clsx('rounded-xl p-6')}>
                    <input type="hidden" value={selectedOffer.id} {...register('offer_id')} />
                    <div className="flex flex-col gap-4">
                        <UiSelectWithGraphics
                            label={t('Payment Type')}
                            options={itemsForOptions}
                            selected={selectedPaymentType}
                            setSelected={(value) => setSelectedOptionHandler(value)}
                        />
                        {selectedPaymentType == PaymentType.Individual && (
                            <>
                                <span>"Ihr gewählter Abrechnungsmodus ist "</span>
                                <UiTextarea
                                    {...register('custom_text')}
                                    placeholder={t('Enter custom text...')}
                                    rows={4}
                                />
                            </>
                        )}
                    </div>
                </div>
            </UiFormInner>

            <UiFormActionsBar
                primaryText={isSubmitting ? t('Executing...') : t('Execute')}
                onPrimary={handleSubmit(onSubmitHandler, onErrorHandler)}
                loading={isSubmitting}
            />
        </form>
    )
}

export default BookingSellerPaymentTypeModal
